import React, { useEffect } from "react";
import { connect } from "react-redux";

import { EntityType } from "@mapmycustomers/shared/enum";
import Activity from "@mapmycustomers/shared/types/entity/Activity";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import TableListView from "@app/component/view/ListView/TableListView";
import { RootState } from "@app/store/rootReducer";
import { subscribe, unsubscribe } from "@app/store/uiSync/actions";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import activityFieldModel from "@app/util/fieldModel/ActivityFieldModel";

import { activityListViewAnalytics } from "../../activityTableAnalytics";
import ViewMode from "../../enum/ViewMode";
import {
  fetchList,
  subscribeOnViewStateUpdating,
  unsubscribeOnViewStateUpdating,
} from "../../store/actions";
import {
  getListViewState,
  getTotalFilteredRecordsCount,
  getTotalRecordsCount,
} from "../../store/selectors";
import Subheader from "../Subheader";

import styles from "./ActivityListPage.module.scss";
import ActivityTable from "./ActivityTable";
import FilterBar from "./component/FilterBar";
import SelectBar from "./SelectBar";

interface Props {
  listFetcher: typeof fetchList.request;
  listViewState: ViewState;
  subscribe: typeof subscribe;
  subscribeOnViewStateUpdating: typeof subscribeOnViewStateUpdating;
  totalFilteredRecords: number;
  totalRecords: number;
  unsubscribe: typeof unsubscribe;
  unsubscribeOnViewStateUpdating: typeof unsubscribeOnViewStateUpdating;
}

const ActivityListPage: React.FC<Props> = ({
  listFetcher,
  listViewState,
  subscribe,
  subscribeOnViewStateUpdating,
  totalFilteredRecords,
  totalRecords,
  unsubscribe,
  unsubscribeOnViewStateUpdating,
}) => {
  useEffect(() => {
    const reFetchData = () => listFetcher({ request: {} });
    const listener = {
      onAdd: reFetchData,
      onDelete: reFetchData,
      onUpdate: reFetchData,
    };
    subscribe({ entityTypes: [EntityType.ACTIVITY], listener });
    return () => {
      unsubscribe({ entityTypes: [EntityType.ACTIVITY], listener });
    };
  }, [subscribe, unsubscribe, listFetcher]);

  useEffect(() => {
    const reFetchData = () => listFetcher({ request: {} });
    const callback = (state: Partial<ViewState>) => {
      listFetcher({ request: state });
      reFetchData();
    };
    subscribeOnViewStateUpdating({ callback });
    return () => {
      unsubscribeOnViewStateUpdating();
    };
  }, [listFetcher, subscribeOnViewStateUpdating, unsubscribeOnViewStateUpdating]);

  return (
    <AnalyticsContext.Provider value={activityListViewAnalytics}>
      <TableListView<Activity>
        entityType={EntityType.ACTIVITY}
        fieldModel={activityFieldModel}
        listFetcher={listFetcher}
        listViewState={listViewState}
        totalRecords={totalRecords}
      >
        <Subheader
          totalFilteredRecords={totalFilteredRecords}
          totalRecords={totalRecords}
          viewMode={ViewMode.LIST}
        />
        <div className={styles.contentWrapper}>
          <FilterBar />
          <SelectBar />
          <ActivityTable />
        </div>
      </TableListView>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  listViewState: getListViewState(state),
  totalFilteredRecords: getTotalFilteredRecordsCount(state),
  totalRecords: getTotalRecordsCount(state),
});

const mapDispatchToProps = {
  listFetcher: fetchList.request,
  subscribe,
  subscribeOnViewStateUpdating,
  unsubscribe,
  unsubscribeOnViewStateUpdating,
};

export default connect(mapStateToProps, mapDispatchToProps)(ActivityListPage);
